import { isObject } from './isObject';

/**
 * Tree: Iterates through an object performing a callback on
 * the object. Can be used to transform it by overwriting
 * the source[key] in the transform function.
 *
 * Used especially in uTheory-api for template transformations
 * of data types
 *
 * @param source - the source object to iterate down the tree on.
 * @param transform - function to call on each property, or omit.
 *    To overwrite the prop, you must call object[index] = ... within transform.
 *    Calls transformFunction on each property in sourceObject,
 *    passing it: (value, index, sourceObject)
 * @param deep - whether to perform a deep tree iteration
 *
 */
export function tree<T>(
  source: T,
  transform: TransformFunction,
  deep: boolean,
  set = new Set(),
  priorKeys: (string | number)[] = []
): T {
  Object.keys(source).forEach((key) => {
    const item = source[key as keyof T];
    // Let's not get ourselves in an infinite loop here!
    if (set.has(item)) return;
    if (isObject(item) || Array.isArray(item)) {
      set.add(item);
    }
    if (transform) transform(item, key as any, source, priorKeys);
    if (deep && (isObject(item) || Array.isArray(item))) {
      tree(item, transform, deep, set, priorKeys.concat(key));
    }
  });
  return source;
}

type TransformFunction = <T>(
  item: any,
  key: keyof T,
  source: T,
  priorKeys: (string | number | symbol)[]
) => void;
